import { useRef, useEffect } from "react";

// Mouse drag-to-scroll with momentum for desktop.
// Uses delta-based scrolling (not absolute) so it doesn't fight with the infinite-loop jump.
function useDragScroll(ref) {
  const dragState = useRef(null);
  const raf = useRef(null);
  const didDragRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const cancelMomentum = () => {
      if (raf.current) {
        cancelAnimationFrame(raf.current);
        raf.current = null;
      }
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      cancelMomentum();
      didDragRef.current = false;
      dragState.current = {
        lastX: e.pageX,
        lastTime: Date.now(),
        velocity: 0,
        totalMoved: 0,
      };
      el.style.userSelect = "none";
    };

    const onMouseMove = (e) => {
      const s = dragState.current;
      if (!s) return;
      const dx = s.lastX - e.pageX; // positive = scroll right
      s.totalMoved += Math.abs(dx);
      if (s.totalMoved > 4) didDragRef.current = true;
      const now = Date.now();
      const dt = now - s.lastTime || 1;
      s.velocity = dx / dt; // px/ms
      s.lastX = e.pageX;
      s.lastTime = now;
      el.scrollLeft += dx; // delta only — won't fight infinite-loop jumps
    };

    const onMouseUp = () => {
      const s = dragState.current;
      if (!s) return;
      let velocity = s.velocity;
      dragState.current = null;
      el.style.userSelect = "";

      if (Math.abs(velocity) > 0.05) {
        const step = () => {
          velocity *= 0.92;
          el.scrollLeft += velocity * 16;
          if (Math.abs(velocity) > 0.05) {
            raf.current = requestAnimationFrame(step);
          } else {
            raf.current = null;
          }
        };
        raf.current = requestAnimationFrame(step);
      }
    };

    const onClickCapture = (e) => {
      if (didDragRef.current) {
        e.stopPropagation();
        didDragRef.current = false;
      }
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    el.addEventListener("click", onClickCapture, true);

    return () => {
      cancelMomentum();
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("click", onClickCapture, true);
    };
  }); // re-attach after every render so ref.current is always fresh
}

export default useDragScroll;
